// ---------------------------------------------------------------------------
// Shared: Number Formatting
//
// Used by both the renderer (displaying values on slides) and crystallizer
// (formatting values in schema descriptions / prompt text).
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS = { USD: "$", EUR: "€", GBP: "£", INR: "₹", JPY: "¥", CNY: "¥", CHF: "CHF " };

function ctxSymbol(ctx) {
  return CURRENCY_SYMBOLS[ctx?.currency] ?? "$";
}

// Shared money formatter. `alreadyMillions` says the incoming value is
// expressed in millions (crystallized financial data usually is) — convert to
// absolute before picking the B/M/K band so the band never lies.
function fmtMoney(value, symbol, alreadyMillions) {
  const absolute = alreadyMillions ? value * 1e6 : value;
  const abs = Math.abs(absolute);
  if (abs >= 1e9) return `${symbol}${(absolute / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${symbol}${(absolute / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${symbol}${(absolute / 1e3).toFixed(1)}K`;
  return `${symbol}${Number(absolute).toFixed(0)}`;
}

const FMT = {
  /**
   * Currency formatting. Symbol comes from the data context's currency when
   * provided (EUR → €, INR → ₹, …), defaulting to $. Values are absolute
   * unless the context says they're expressed in millions.
   * e.g. 4200000000 → "$4.2B", 37500000 → "$37.5M", 5000 → "$5K", 42 → "$42"
   */
  currency(value, ctx) {
    if (value == null) return "";
    return fmtMoney(value, ctxSymbol(ctx), ctx?.valuesUnit === "million");
  },

  /**
   * GBP currency formatting (absolute values, not pre-divided). Explicit
   * format — always £ regardless of data currency.
   * e.g. 42 → "£42.00", 4200 → "£4.2K", 4200000 → "£4.2M"
   */
  currency_gbp(value) {
    if (value == null) return "";
    const abs = Math.abs(value);
    if (abs >= 1e9) return `£${(value / 1e9).toFixed(1)}bn`;
    if (abs >= 1e6) return `£${(value / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `£${(value / 1e3).toFixed(1)}K`;
    return `£${Number(value).toFixed(value < 100 ? 2 : 0)}`;
  },

  /**
   * Currency formatting — values already expressed in MILLIONS. Symbol from
   * the data context's currency when provided, defaulting to $.
   * e.g. 2196 → "$2.2B", 540 → "$540M"
   */
  currency_m(value, ctx) {
    if (value == null) return "";
    return fmtMoney(value, ctxSymbol(ctx), true);
  },

  /**
   * GBP currency formatting — values already expressed in MILLIONS. Explicit
   * format — always £.
   * e.g. 2196 → "£2.2bn", 540 → "£540M"
   */
  currency_gbp_m(value) {
    if (value == null) return "";
    const abs = Math.abs(value);
    if (abs >= 1000) return `£${(value / 1000).toFixed(1)}bn`;
    return `£${Math.round(value)}M`;
  },

  /** Percentage to 1 decimal place. e.g. 12.345 → "12.3%" */
  pct_1dp(value) {
    if (value == null) return "";
    return `${Number(value).toFixed(1)}%`;
  },

  /** Number to 1 decimal place. */
  num_1dp(value) {
    if (value == null) return "";
    return Number(value).toFixed(1);
  },

  /** Number to 0 decimal places (rounded). */
  num_0dp(value) {
    if (value == null) return "";
    return Math.round(Number(value)).toString();
  },

  /**
   * Compact magnitude formatting WITHOUT a currency symbol — used as the
   * fallback when a tile has no explicit format and the value is a large raw
   * number (e.g. an LLM returning 5000000000 where a formatted string was
   * asked for). 5000000000 → "5B", 253000000 → "253M", 12000 → "12K".
   */
  num_compact(value) {
    if (value == null) return "";
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    const abs = Math.abs(num);
    const fmt = (v) => v.toFixed(1).replace(/\.0$/, "");
    if (abs >= 1e9) return `${fmt(num / 1e9)}B`;
    if (abs >= 1e6) return `${fmt(num / 1e6)}M`;
    if (abs >= 1e4) return `${fmt(num / 1e3)}K`;
    return String(num);
  },

  /** Date string passthrough. */
  date(value) {
    if (value == null) return "";
    return value;
  },
};

/**
 * Format a value using a named format function.
 *
 * @param {number|string|null} value
 * @param {string} format — one of: currency, currency_gbp, currency_m,
 *   currency_gbp_m, pct_1dp, num_1dp, num_0dp, date
 * @param {object} [ctx] — optional data context: { currency: "EUR"|"INR"|…,
 *   valuesUnit: "million"|"raw" }. Generic currency formats use it to resolve
 *   the symbol and value scale from the data instead of assuming $.
 * @returns {string}
 */
export function formatValue(value, format, ctx) {
  if (value == null) return "";
  const fn = FMT[format];
  return fn ? fn(value, ctx) : String(value);
}

export { FMT };
